import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CylinderEntryRow } from '../components/CylinderEntryRow';
import { Icon } from '../components/Icon';
import {
  classifyCht,
  maxCht,
  referenceCylinder,
} from '../lib/readings';
import { useEngineStore } from '../state/engineStore';

type CalloutKind = 'info' | 'warn' | 'danger';

type WizardStepDef = {
  id: string;
  phase: string;
  title: string;
  body: string;
  checks?: string[];
  callout: { kind: CalloutKind; title: string; body: string };
};

const WIZARD_STEPS: WizardStepDef[] = [
  {
    id: 'verify',
    phase: '01',
    title: 'Verify cruise configuration',
    body:
      'Before leaning, confirm you are stabilised in cruise. Power must be at or below 65 % so the Red Box disappears — then any mixture is safe.',
    checks: [
      'Level flight, cruise altitude captured',
      'Stable for ≥ 2 min (CHT has settled)',
      'Power ≤ 65 % — WOT OK if density altitude ≥ 8,000 ft, else throttle back',
      'Propeller: cruise RPM',
      'Cowl flaps: as required',
    ],
    callout: {
      kind: 'info',
      title: 'Why ≤ 65 % power?',
      body:
        'Below 65 % the peak cylinder pressure is too low to harm the engine at any mixture. The Red Box only exists at high power.',
    },
  },
  {
    id: 'baseline',
    phase: '02',
    title: 'Enter baseline EGTs',
    body:
      'Before you start leaning, type the current EGT of every cylinder from your engine monitor. The app uses these to detect which cylinder peaks last as you lean.',
    callout: {
      kind: 'info',
      title: 'Why a baseline?',
      body:
        'The app has no idea what the engine is doing unless you tell it. Entering a baseline gives every cylinder a starting EGT so the peak-tracking can compare against it.',
    },
  },
  {
    id: 'transit',
    phase: '03',
    title: 'Transit the Red Box quickly',
    body:
      'At > 65 % power, every mixture from peak to roughly 100 °F Rich of Peak puts damaging pressure on the cylinders (the Red Box). Pull the mixture in one smooth motion past peak into lean territory — over-lean deliberately so EGTs climb past peak and start dropping.',
    callout: {
      kind: 'warn',
      title: 'Speed matters',
      body:
        'At cruise power the Red Box is visited for seconds, not minutes. Do not pause mid-transit to type values — finish the pull, then enter readings.',
    },
  },
  {
    id: 'peak',
    phase: '04',
    title: 'Find peak, then lean back to target',
    body:
      'Enrich slowly and re-enter EGTs every few seconds. The app flags a cylinder as "peaked" the moment its EGT drops below the highest value you have entered. The last cylinder to peak is your reference.',
    callout: {
      kind: 'info',
      title: 'Do not settle at peak',
      body:
        'Peak EGT is the hottest, worst mixture for the engine. As soon as the reference cylinder peaks, lean back again toward your LOP target in step 05.',
    },
  },
  {
    id: 'settle',
    phase: '05',
    title: 'Set the LOP target',
    body:
      'Choose a target offset below the reference peak. 25–50 °F LOP is typical for well-balanced engines — cooler, cleaner, leaner than any ROP setting. CHT lags EGT by up to 2 minutes, so let the temperatures settle before judging.',
    callout: {
      kind: 'info',
      title: 'Default: 30 °F LOP',
      body: 'Good efficiency without sacrificing smoothness on well-balanced engines.',
    },
  },
  {
    id: 'confirm',
    phase: '06',
    title: 'Confirm CHT under limits',
    body:
      'Wait 2 minutes for CHT to settle, then enter the current CHT of every cylinder. The app flags any cylinder above your personal red line.',
    callout: {
      kind: 'info',
      title: 'What you captured',
      body:
        'Reference cylinder, peak EGT, final mixture delta, and max CHT — all in the session. Nothing is stored after the browser tab closes.',
    },
  },
];

const LOP_OPTIONS = [15, 25, 30, 40, 50];

export function WizardScreen() {
  const navigate = useNavigate();
  const {
    config,
    readings,
    setCylinderEgt,
    setCylinderCht,
    resetPeaks,
  } = useEngineStore();
  const { cylinders } = readings;

  const [step, setStep] = useState(0);
  const [checks, setChecks] = useState<boolean[]>(
    WIZARD_STEPS[0].checks!.map(() => false),
  );
  const [transitConfirmed, setTransitConfirmed] = useState(false);
  const [lopTarget, setLopTarget] = useState(30);

  const ref = referenceCylinder(cylinders);
  const refPeakEgt = ref?.peakEgt;
  const refCurrent =
    ref && cylinders.find((c) => c.id === ref.id)?.egt;
  const refDelta =
    refPeakEgt !== undefined && refCurrent !== undefined
      ? refPeakEgt - refCurrent
      : undefined;

  const entered = cylinders.filter((c) => c.egt !== undefined).length;
  const peakedCount = cylinders.filter((c) => c.peakedAt !== undefined).length;
  const allEntered = entered === config.cylinderCount;
  const refPeaked = ref !== undefined && ref.peakedAt !== undefined;

  const hottest = maxCht(cylinders);
  const chtChecked = cylinders.filter((c) => c.cht !== undefined).length;
  const anyChtOverRed =
    hottest !== undefined &&
    classifyCht(hottest.cht, config.chtLimits) === 'danger';

  const s = WIZARD_STEPS[step];

  const canAdvance = useMemo(() => {
    switch (step) {
      case 0:
        return checks.every(Boolean);
      case 1:
        return allEntered;
      case 2:
        return transitConfirmed;
      case 3:
        return refPeaked;
      case 4:
        return refDelta !== undefined && Math.abs(refDelta - lopTarget) <= 5;
      case 5:
        return chtChecked === config.cylinderCount && !anyChtOverRed;
      default:
        return false;
    }
  }, [
    step,
    checks,
    allEntered,
    transitConfirmed,
    refPeaked,
    refDelta,
    lopTarget,
    chtChecked,
    config.cylinderCount,
    anyChtOverRed,
  ]);

  const advanceHint =
    step === 0
      ? 'Tick all checklist items'
      : step === 1
        ? `Enter EGT for every cylinder (${entered}/${config.cylinderCount})`
        : step === 2
          ? 'Confirm you have pulled the mixture past peak'
          : step === 3
            ? refPeaked
              ? ''
              : `Keep entering EGTs — reference cylinder has not peaked yet (${peakedCount}/${config.cylinderCount} peaked)`
            : step === 4
              ? refDelta === undefined
                ? 'Enter current EGT of the reference cylinder'
                : `Target ${lopTarget} °F LOP — you are at ${Math.round(refDelta)} °F, lean or enrich to match`
              : step === 5
                ? anyChtOverRed
                  ? 'A cylinder is above your red line — reduce power, enrich, then re-check'
                  : `Enter CHT for every cylinder (${chtChecked}/${config.cylinderCount})`
                : '';

  return (
    <div className="wiz-stage">
      <div className="wiz-main">
        <div className="step-track">
          {WIZARD_STEPS.map((_, i) => (
            <div
              key={i}
              className={`step-dot ${i < step ? 'done' : i === step ? 'active' : ''}`}
            />
          ))}
        </div>

        <div>
          <div className="step-header">
            <span className="step-number">STEP {s.phase} / 06</span>
            {step === 3 && refPeaked && ref && (
              <span className="chip ok">
                <span className="dot" /> Reference Cyl #{ref.id} peaked at{' '}
                {Math.round(ref.peakEgt ?? 0)} °F
              </span>
            )}
            {step === 5 && anyChtOverRed && hottest && (
              <span className="chip danger">
                <span className="dot" /> Cyl #{hottest.id} above red line
              </span>
            )}
          </div>
          <h2 className="step-title">{s.title}</h2>
          <p className="step-body">{s.body}</p>
        </div>

        {step === 0 && s.checks && (
          <div className="card">
            <div className="card-title">Pre-lean checklist</div>
            <div className="col gap-sm">
              {s.checks.map((t, i) => (
                <label
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 0',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                  onClick={() => setChecks((cs) => cs.map((c, j) => (j === i ? !c : c)))}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      border: checks[i]
                        ? '1.5px solid var(--ok)'
                        : '1.5px solid var(--line)',
                      background: checks[i] ? 'var(--ok)' : 'var(--surface)',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {checks[i] && <Icon name="check" size={14} color="#fff" />}
                  </span>
                  <span style={{ color: checks[i] ? 'var(--ink)' : 'var(--ink-2)' }}>{t}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="card">
            <div className="row between" style={{ marginBottom: 12 }}>
              <div className="card-title" style={{ margin: 0 }}>
                Baseline EGT per cylinder
              </div>
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => resetPeaks()}
                title="Clear peak history to start a fresh leaning cycle"
              >
                Reset peaks
              </button>
            </div>
            <CylinderEntryRow
              cylinders={cylinders}
              field="egt"
              onChange={setCylinderEgt}
            />
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 12 }}>
              Typical ROP cruise baseline for the reference cylinder: 1350–1450 °F.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="card">
            <div className="card-title">Transit complete?</div>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', margin: '0 0 16px' }}>
              Do the pull now, in one smooth motion, past peak into LOP territory. Come
              back here once the mixture is set and the EGTs stopped changing quickly.
            </p>
            <button
              type="button"
              className={`btn ${transitConfirmed ? 'ghost' : 'primary'}`}
              onClick={() => setTransitConfirmed((v) => !v)}
            >
              {transitConfirmed ? '✓ Transit confirmed' : 'I have pulled the mixture past peak'}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="card">
            <div className="row between" style={{ marginBottom: 12 }}>
              <div className="card-title" style={{ margin: 0 }}>
                Current EGT — enrich slowly and re-enter
              </div>
              <span className="chip">
                <span className="dot" /> {peakedCount}/{config.cylinderCount} peaked
              </span>
            </div>
            <CylinderEntryRow
              cylinders={cylinders}
              field="egt"
              onChange={setCylinderEgt}
            />
            {ref && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 10,
                  background: refPeaked ? 'var(--ok-soft)' : 'var(--surface-2)',
                  fontSize: 13,
                  color: refPeaked ? 'oklch(0.4 0.14 152)' : 'var(--ink-2)',
                }}
              >
                Reference candidate: Cyl #{ref.id}, peak so far{' '}
                {ref.peakEgt !== undefined ? `${Math.round(ref.peakEgt)} °F` : '—'}.
                {refPeaked
                  ? ' This cylinder has started dropping from its peak.'
                  : ' Still climbing — keep enriching.'}
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="card">
            <div className="card-title">LOP target offset</div>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              {LOP_OPTIONS.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setLopTarget(v)}
                  className="btn sm"
                  style={{
                    background: lopTarget === v ? 'var(--ink)' : 'var(--surface)',
                    color: lopTarget === v ? '#fff' : 'var(--ink)',
                    borderColor: lopTarget === v ? 'var(--ink)' : 'var(--line)',
                    minWidth: 72,
                  }}
                >
                  {v} °F LOP
                </button>
              ))}
            </div>

            <div style={{ marginTop: 16 }}>
              <CylinderEntryRow
                cylinders={cylinders}
                field="egt"
                onChange={setCylinderEgt}
                label="Current EGT (re-enter after each mixture adjustment)"
              />
            </div>

            <div
              style={{
                marginTop: 14,
                fontSize: 13,
                color: 'var(--ink-2)',
                lineHeight: 1.5,
              }}
            >
              {refDelta === undefined ? (
                <>Enter EGT for the reference cylinder (#{ref?.id ?? '—'}) to see your offset.</>
              ) : (
                <>
                  Reference cylinder is at{' '}
                  <span className="num-sm" style={{ color: 'var(--ink)' }}>
                    {Math.round(refDelta)} °F LOP
                  </span>{' '}
                  · target{' '}
                  <span className="num-sm" style={{ color: 'var(--primary)' }}>
                    {lopTarget} °F LOP
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="card">
            <div className="card-title">CHT per cylinder</div>
            <CylinderEntryRow
              cylinders={cylinders}
              field="cht"
              onChange={setCylinderCht}
            />
            {hottest && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 10,
                  background: anyChtOverRed ? 'var(--danger-soft)' : 'var(--ok-soft)',
                  fontSize: 13,
                  color: anyChtOverRed
                    ? 'oklch(0.42 0.18 25)'
                    : 'oklch(0.4 0.14 152)',
                }}
              >
                Max CHT: Cyl #{hottest.id} at {Math.round(hottest.cht)} °F
                {anyChtOverRed
                  ? ` — above your ${config.chtLimits.warn} °F red line.`
                  : ` — inside your limits (red line ${config.chtLimits.warn} °F).`}
              </div>
            )}
          </div>
        )}

        <div className="row between mt-auto" style={{ paddingTop: 16 }}>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setStep((v) => Math.max(0, v - 1))}
            disabled={step === 0}
          >
            Back
          </button>
          <div className="row gap-sm" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {!canAdvance && advanceHint && <span>{advanceHint}</span>}
          </div>
          {step < WIZARD_STEPS.length - 1 ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setStep((v) => v + 1)}
              disabled={!canAdvance}
            >
              Continue <Icon name="chev" size={14} color="#fff" />
            </button>
          ) : (
            <button
              type="button"
              className="btn accent"
              onClick={() => navigate('/monitor')}
              disabled={!canAdvance}
            >
              <Icon name="check" size={16} color="#fff" /> Finish — open monitor
            </button>
          )}
        </div>
      </div>

      <div className="wiz-side">
        <div className={`callout ${s.callout.kind}`}>
          <Icon name={s.callout.kind === 'warn' ? 'alert' : 'spark'} size={18} />
          <div>
            <div className="callout-title">{s.callout.title}</div>
            <div>{s.callout.body}</div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Engine snapshot</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
            }}
          >
            <div>
              <div className="stat-label">Max CHT</div>
              <div
                className="num-md"
                style={{
                  color: hottest
                    ? classifyCht(hottest.cht, config.chtLimits) === 'danger'
                      ? 'var(--danger)'
                      : classifyCht(hottest.cht, config.chtLimits) === 'warn'
                        ? 'var(--warn)'
                        : 'var(--ok)'
                    : 'var(--ink-3)',
                  marginTop: 4,
                }}
              >
                {hottest ? Math.round(hottest.cht) : '—'}
                {hottest && <span className="unit">°F</span>}
              </div>
            </div>
            <div>
              <div className="stat-label">Reference Cyl</div>
              <div className="num-md" style={{ marginTop: 4 }}>
                {ref ? `#${ref.id}` : '—'}
              </div>
            </div>
            <div>
              <div className="stat-label">Peak EGT (ref)</div>
              <div className="num-md" style={{ marginTop: 4 }}>
                {ref?.peakEgt !== undefined ? `${Math.round(ref.peakEgt)}` : '—'}
                {ref?.peakEgt !== undefined && <span className="unit">°F</span>}
              </div>
            </div>
            <div>
              <div className="stat-label">Δ below peak</div>
              <div
                className="num-md"
                style={{
                  color: refDelta !== undefined && refDelta > 10 ? 'var(--ok)' : 'var(--ink)',
                  marginTop: 4,
                }}
              >
                {refDelta !== undefined ? `${Math.round(refDelta)}` : '—'}
                {refDelta !== undefined && <span className="unit">°F</span>}
              </div>
            </div>
            <div>
              <div className="stat-label">Peaked</div>
              <div className="num-md" style={{ marginTop: 4 }}>
                {peakedCount} / {config.cylinderCount}
              </div>
            </div>
            <div>
              <div className="stat-label">EGTs entered</div>
              <div className="num-md" style={{ marginTop: 4 }}>
                {entered} / {config.cylinderCount}
              </div>
            </div>
          </div>
        </div>

        <div className="card card-ghost flex-1">
          <div className="card-title">The 6 steps</div>
          <div className="col gap-sm">
            {WIZARD_STEPS.map((w, i) => (
              <div
                key={i}
                className="bullet"
                style={{
                  opacity: i > step ? 0.45 : 1,
                  color: i === step ? 'var(--ink)' : 'var(--ink-2)',
                }}
              >
                <div className="idx">{i < step ? '✓' : w.phase}</div>
                <div style={{ fontWeight: i === step ? 600 : 500 }}>{w.title}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
